import { Button, ButtonProps } from '@chronosphereio/chrono-ui';
import {
  areTimeRangeInputValuesEqual,
  getTimeRangeFromDurationString,
  isDurationString,
  TimeRangeInputValue,
} from '@chronosphereio/core';
import { useState } from 'react';
import { DashboardMetadata, DashboardResource } from '@/core';
import { useFindMatchingViews } from '@/dashboards/utils/saved-view-utils';
import { useTimeRange } from '@/plugin-system/runtime/TimeRangeProviderV2';
import {
  useDashboard,
  useDashboardMetadata,
  useEditMode,
  useSaveChangesConfirmationDialog,
  useSavedViews,
  useTemplateVariableActions,
  useTemplateVariableViewState,
  useUpdateDashboardDefaults,
} from '../../context';

export interface SaveDashboardButtonProps extends Pick<ButtonProps, 'fullWidth' | 'variant'> {
  onSave?: ({ config, metadata }: { config: DashboardResource; metadata: DashboardMetadata }) => Promise<unknown>;
  isDisabled: boolean;
  label?: string;
}

export const SaveDashboardButton = ({
  onSave,
  isDisabled,
  variant = 'primary',
  label = 'Save',
}: SaveDashboardButtonProps) => {
  const [isSavingDashboard, setSavingDashboard] = useState<boolean>(false);
  const { dashboard } = useDashboard();
  const { metadata } = useDashboardMetadata();
  const { getSavedVariablesStatus } = useTemplateVariableActions();
  const { isSavedVariableModified } = getSavedVariablesStatus();
  const { timeRange: activeDashboardTimeRange } = useTimeRange();
  const { setEditMode } = useEditMode();
  const { openSaveChangesConfirmationDialog, closeSaveChangesConfirmationDialog } = useSaveChangesConfirmationDialog();
  const { currentView, defaultView } = useTemplateVariableViewState();
  const savedViews = useSavedViews();
  const matchingViews = useFindMatchingViews(savedViews, currentView, defaultView);
  const { updateDashboardDefaults } = useUpdateDashboardDefaults();

  const onSaveButtonClick = () => {
    // Determine if saved time is different than current dashboard time range
    let savedTime: TimeRangeInputValue | undefined = undefined;
    if (dashboard.spec.time?.start && dashboard.spec.time?.end) {
      savedTime = {
        start: new Date(dashboard.spec.time.start),
        end: new Date(dashboard.spec.time.end),
      };
    } else if (dashboard.spec.duration && isDurationString(dashboard.spec.duration)) {
      savedTime = getTimeRangeFromDurationString(dashboard.spec.duration);
    }
    const isSavedTimeRangeModified =
      savedTime !== undefined ? !areTimeRangeInputValuesEqual(activeDashboardTimeRange, savedTime) : false;

    // Save dashboard
    // - if active timeRange from plugin-system is different from currently saved
    // - or if the saved variables are different from currently saved
    // Skip the variable dialog if the current values already match a saved view
    // (the user already handled the changes via the saved view selector).
    const hasUnhandledVariableChanges = isSavedVariableModified && matchingViews.length === 0;
    if (isSavedTimeRangeModified || hasUnhandledVariableChanges) {
      openSaveChangesConfirmationDialog({
        onSaveChanges: (saveDefaultTimeRange, saveDefaultVariables, savedViewOptions) => {
          const updatedDashboard = updateDashboardDefaults({
            updateTimeRange: saveDefaultTimeRange,
            updateVariableDefaults: saveDefaultVariables,
            defaultViewName: savedViewOptions?.defaultViewName,
            createSavedView: savedViewOptions?.createSavedView,
            updateEvents: true,
          });
          void saveDashboard(updatedDashboard);
        },
        onCancel: () => {
          closeSaveChangesConfirmationDialog();
        },
        isSavedTimeRangeModified,
        isSavedVariableModified: hasUnhandledVariableChanges,
      });
    } else {
      void saveDashboard();
    }
  };

  const saveDashboard = async (configOverride?: DashboardResource) => {
    if (!onSave) {
      return;
    }
    setSavingDashboard(true);
    try {
      await onSave({ config: configOverride ?? dashboard, metadata });
      closeSaveChangesConfirmationDialog();
      setEditMode(false);
    } catch (_err) {
      // Do nothing with error
    }
    setSavingDashboard(false);
  };

  return (
    <Button variant={variant} onClick={onSaveButtonClick} disabled={isDisabled || isSavingDashboard}>
      {label}
    </Button>
  );
};
